import { imageUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";

const Swatch = ({ hex, name, size = "h-14 w-14" }) => (
  <div className="flex flex-col items-center gap-2 w-[76px]">
    <div
      className={`${size} rounded-full border border-black/10`}
      style={{ backgroundColor: hex || "#ddd" }}
      title={name}
    />
    <span className="text-[9px] leading-tight text-center text-neutral-600">{name}</span>
  </div>
);

const Page = ({ children, index, name, testId }) => (
  <section
    data-testid={testId}
    className="report-page mx-auto border border-border shadow-[0_1px_40px_rgba(0,0,0,0.06)] px-[18mm] py-[16mm] flex flex-col"
  >
    <header className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-8">
      <Logo className="h-7" />
      <span className="text-[9px] uppercase tracking-[0.25em] text-neutral-500">
        Style DNA · {name} · Seite {index}
      </span>
    </header>
    <div className="flex-1">{children}</div>
    <footer className="mt-8 pt-4 border-t border-neutral-200 flex justify-between text-[8px] uppercase tracking-[0.2em] text-neutral-400">
      <span>Figaro Busch — seit 1974</span>
      <span>Persönliche Stilberatung</span>
    </footer>
  </section>
);

const Block = ({ title, children, className = "" }) => (
  <div className={`mb-7 ${className}`}>
    <h3 className="text-[10px] uppercase tracking-[0.22em] font-semibold text-neutral-500 mb-3">{title}</h3>
    {children}
  </div>
);

const Fact = ({ label, value }) => (
  <div className="border-t border-neutral-200 pt-2">
    <p className="text-[8px] uppercase tracking-[0.2em] text-neutral-400">{label}</p>
    <p className="text-[11px] text-neutral-800 mt-1 leading-snug">{value || "—"}</p>
  </div>
);

const LookImage = ({ path, caption }) => (
  <figure className="w-full">
    <div className="aspect-[3/4] bg-neutral-100 overflow-hidden rounded-sm border border-neutral-200">
      {path ? <img src={imageUrl(path)} alt={caption} className="h-full w-full object-cover" /> : null}
    </div>
    <figcaption className="mt-2 text-[9px] uppercase tracking-[0.15em] text-neutral-600">{caption}</figcaption>
  </figure>
);

export const StyleReport = ({ analysis }) => {
  const r = analysis.result || {};
  const m = r.merkmale || {};
  const looks = analysis.looks || [];
  const frisurLooks = looks.filter((l) => l.kind === "frisur");
  const farbLooks = looks.filter((l) => l.kind === "haarfarbe");
  const isDamen = analysis.gender === "damen";
  const portrait = analysis.photo_paths?.[0];
  const dateStr = new Date(analysis.created_at).toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-10" data-testid="style-report">
      {/* ---------- Seite 1 ---------- */}
      <Page index={1} name={analysis.customer_name} testId="report-page-1">
        <div className="grid grid-cols-[38%_1fr] gap-8">
          <div>
            <div className="aspect-[3/4] bg-neutral-100 overflow-hidden rounded-sm border border-neutral-200">
              {portrait ? (
                <img src={imageUrl(portrait)} alt={analysis.customer_name} className="h-full w-full object-cover" />
              ) : null}
            </div>
            <div className="mt-5 space-y-3">
              <Fact label="Gesichtsform" value={m.gesichtsform} />
              <Fact label="Hautunterton" value={m.hautunterton} />
              <Fact label="Augenfarbe" value={m.augenfarbe} />
              <Fact label="Farbtyp" value={m.farbtyp} />
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{dateStr}</p>
            <h1 className="font-display text-[34px] leading-[1.05] font-light tracking-tight mt-2 mb-1">
              {analysis.customer_name}
            </h1>
            <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-6">
              Persönliche Stilberatung — {isDamen ? "Damen" : "Herren"}
            </p>
            <p className="text-[11px] leading-relaxed text-neutral-700 mb-7">{r.zusammenfassung}</p>

            <Block title="Gesichtsform im Detail">
              <p className="text-[11px] leading-relaxed text-neutral-700">{m.gesichtsform_begruendung}</p>
            </Block>

            <Block title="Empfohlene Haarlängen">
              <ol className="space-y-2">
                {(r.haarlaengen || []).map((h, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-display text-[13px] text-neutral-400">0{i + 1}</span>
                    <div>
                      <p className="text-[11px] font-semibold text-neutral-900">{h.name}</p>
                      <p className="text-[10px] leading-relaxed text-neutral-600">{h.begruendung}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Block>

            <Block title="Pony-Empfehlung">
              <p className="text-[11px] font-semibold text-neutral-900">{r.pony?.empfehlung}</p>
              <p className="text-[10px] leading-relaxed text-neutral-600">{r.pony?.begruendung}</p>
            </Block>
          </div>
        </div>

        <Block title="Deine drei Frisuren" className="mt-4">
          <div className="grid grid-cols-3 gap-5">
            {(r.frisuren || []).map((f, i) => (
              <div key={i}>
                <LookImage path={frisurLooks.find((l) => l.index === i)?.path} caption={f.name} />
                <p className="mt-2 text-[10px] leading-relaxed text-neutral-700">{f.beschreibung}</p>
                {f.styling_tipp && (
                  <p className="mt-1 text-[9px] leading-relaxed text-neutral-500 italic">{f.styling_tipp}</p>
                )}
              </div>
            ))}
          </div>
        </Block>
      </Page>

      {/* ---------- Seite 2 ---------- */}
      <Page index={2} name={analysis.customer_name} testId="report-page-2">
        <h2 className="font-display text-[26px] font-light tracking-tight mb-6">Farbe & Pflege</h2>

        <Block title="Deine drei Haarfarben">
          <div className="grid grid-cols-3 gap-5">
            {(r.haarfarben || []).map((c, i) => (
              <div key={i}>
                <LookImage path={farbLooks.find((l) => l.index === i)?.path} caption={c.name} />
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="h-4 w-4 rounded-full border border-black/10"
                    style={{ backgroundColor: c.hex || "#ddd" }}
                  />
                  <span className="text-[9px] text-neutral-500">{c.hex}</span>
                </div>
                <p className="mt-1 text-[10px] leading-relaxed text-neutral-700">{c.beschreibung}</p>
              </div>
            ))}
          </div>
        </Block>

        <div className="grid grid-cols-2 gap-8">
          <Block title="Farbtechniken">
            <ul className="space-y-2">
              {(r.farbtechniken || []).map((t, i) => (
                <li key={i}>
                  <p className="text-[11px] font-semibold text-neutral-900">{t.name}</p>
                  <p className="text-[10px] leading-relaxed text-neutral-600">{t.beschreibung}</p>
                </li>
              ))}
            </ul>
          </Block>

          <Block title="Diese Farben besser vermeiden">
            <div className="flex flex-wrap gap-3 mb-3">
              {(r.vermeiden_farben || []).map((c, i) => (
                <Swatch key={i} hex={c.hex} name={c.name} size="h-10 w-10" />
              ))}
            </div>
            <ul className="space-y-1">
              {(r.vermeiden_farben || []).map((c, i) => (
                <li key={i} className="text-[10px] leading-relaxed text-neutral-600">
                  <span className="font-semibold text-neutral-800">{c.name}:</span> {c.grund}
                </li>
              ))}
            </ul>
          </Block>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <Block title="Styling-Produkte">
            <ul className="space-y-2">
              {(r.styling_produkte || []).map((p, i) => (
                <li key={i}>
                  <p className="text-[11px] font-semibold text-neutral-900">{p.name}</p>
                  <p className="text-[10px] leading-relaxed text-neutral-600">{p.anwendung}</p>
                </li>
              ))}
            </ul>
          </Block>

          {isDamen ? (
            <Block title="Hochsteckfrisuren & Scheitel">
              <ul className="space-y-2 mb-3">
                {(r.damen?.hochsteckfrisuren || []).map((h, i) => (
                  <li key={i}>
                    <p className="text-[11px] font-semibold text-neutral-900">{h.name}</p>
                    <p className="text-[10px] leading-relaxed text-neutral-600">{h.beschreibung}</p>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] leading-relaxed text-neutral-600">
                <span className="font-semibold text-neutral-800">Scheitel:</span> {r.damen?.scheitel}
              </p>
            </Block>
          ) : (
            <Block title="Bart, Fade & Konturen">
              <p className="text-[11px] font-semibold text-neutral-900">{r.herren?.bartform?.name}</p>
              <p className="text-[10px] leading-relaxed text-neutral-600 mb-2">{r.herren?.bartform?.begruendung}</p>
              <p className="text-[10px] leading-relaxed text-neutral-600">
                <span className="font-semibold text-neutral-800">Fade:</span> {r.herren?.fade}
              </p>
              <p className="text-[10px] leading-relaxed text-neutral-600">
                <span className="font-semibold text-neutral-800">Konturen:</span> {r.herren?.konturen}
              </p>
              <p className="text-[10px] leading-relaxed text-neutral-600 mt-2">
                {r.herren?.bart_frisur_kombination}
              </p>
            </Block>
          )}
        </div>

        {isDamen && r.damen?.makeup && (
          <Block title="Make-up & Lippen">
            <div className="grid grid-cols-[1fr_auto] gap-8 items-start">
              <div className="space-y-1">
                <p className="text-[10px] leading-relaxed text-neutral-600">
                  <span className="font-semibold text-neutral-800">Teint:</span> {r.damen.makeup.teint}
                </p>
                <p className="text-[10px] leading-relaxed text-neutral-600">
                  <span className="font-semibold text-neutral-800">Augen:</span> {r.damen.makeup.augen}
                </p>
                <p className="text-[10px] leading-relaxed text-neutral-600">
                  <span className="font-semibold text-neutral-800">Rouge:</span> {r.damen.makeup.rouge}
                </p>
              </div>
              <div className="flex gap-2">
                {(r.damen.lippen || []).map((l, i) => (
                  <Swatch key={i} hex={l.hex} name={l.name} size="h-9 w-9" />
                ))}
              </div>
            </div>
          </Block>
        )}
      </Page>

      {/* ---------- Seite 3 ---------- */}
      <Page index={3} name={analysis.customer_name} testId="report-page-3">
        <h2 className="font-display text-[26px] font-light tracking-tight mb-2">
          Farbtyp {m.farbtyp ? `— ${m.farbtyp}` : ""}
        </h2>
        <p className="text-[11px] leading-relaxed text-neutral-700 mb-6 max-w-[135mm]">{m.farbtyp_begruendung}</p>

        <Block title="Dein Farbpass">
          <div className="flex flex-wrap gap-3">
            {(r.farbpass || []).map((c, i) => (
              <Swatch key={i} hex={c.hex} name={c.name} size="h-12 w-12" />
            ))}
          </div>
        </Block>

        <div className="grid grid-cols-2 gap-8">
          <Block title="Kleidungsstil">
            <p className="text-[10px] leading-relaxed text-neutral-700 mb-3">{r.kleidung?.stil}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(r.kleidung?.farben || []).map((c, i) => (
                <Swatch key={i} hex={c.hex} name={c.name} size="h-9 w-9" />
              ))}
            </div>
            <p className="text-[10px] text-neutral-600">
              <span className="font-semibold text-neutral-800">Muster:</span> {(r.kleidung?.muster || []).join(", ")}
            </p>
            <p className="text-[10px] text-neutral-600">
              <span className="font-semibold text-neutral-800">Stoffe:</span> {(r.kleidung?.stoffe || []).join(", ")}
            </p>
            <p className="text-[10px] text-neutral-600">
              <span className="font-semibold text-neutral-800">Vermeiden:</span>{" "}
              {(r.kleidung?.vermeiden || []).join(", ")}
            </p>
          </Block>

          <Block title="Accessoires">
            <p className="text-[10px] text-neutral-600 mb-2">
              <span className="font-semibold text-neutral-800">Brillenformen:</span> {(r.brillen || []).join(", ")}
            </p>
            <p className="text-[10px] text-neutral-600 mb-2">
              <span className="font-semibold text-neutral-800">Sonnenbrillen:</span>{" "}
              {(r.sonnenbrillen || []).join(", ")}
            </p>
            <p className="text-[10px] text-neutral-600">
              <span className="font-semibold text-neutral-800">Schmuck:</span> {r.schmuckfarben?.empfehlung} —{" "}
              {r.schmuckfarben?.begruendung}
            </p>
          </Block>
        </div>

        <Block title="Gesichtsanalyse">
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(r.gesichtsanalyse || {}).map(([k, v]) => (
              <Fact key={k} label={k.replace(/_/g, " ")} value={v} />
            ))}
          </div>
        </Block>

        <Block title="Lifestyle & Signatur-Look">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Fact label="Beruf" value={r.lifestyle?.beruf_look} />
            <Fact label="Pflegeaufwand" value={r.lifestyle?.pflegeaufwand} />
            <Fact label="Sport & Freizeit" value={r.lifestyle?.sport_freizeit_look} />
            <Fact label="Signatur-Look" value={r.lifestyle?.signatur_look} />
          </div>
        </Block>

        <Block title="Vorher / Nachher">
          <div className="grid grid-cols-2 gap-5">
            <LookImage path={portrait} caption="Ausgangspunkt" />
            <LookImage path={frisurLooks[0]?.path} caption={r.frisuren?.[0]?.name || "Empfehlung"} />
          </div>
        </Block>
      </Page>
    </div>
  );
};
